/**
 * VehicleMatchCard — Fleet-level vehicle and charger match display.
 *
 * Shows extracted specs, best database match with confidence score,
 * and allows the user to override the match.
 *
 * Props:
 *   vehicleSpecs: { battery_capacity_kwh, efficiency_kwh_per_mile, charger_type, charger_power_kw }
 *   vehicleMatch: { best_match: { id, name, score }, alternatives: [...] }
 *   vehicleDatabase: array of vehicles from DB
 *   selectedVehicleId: string (currently selected vehicle ID)
 *   onVehicleSelect: (vehicleId) => void
 */
import React from 'react';
import { Box, Typography, Chip, Autocomplete, TextField, alpha } from '@mui/material';
import EvStationIcon from '@mui/icons-material/EvStation';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import { vortxColors } from '@vortx/shared-ui';

function VehicleMatchCard({
    vehicleSpecs = {},
    vehicleMatch = {},
    vehicleDatabase = [],
    selectedVehicleId,
    onVehicleSelect,
}) {
    const selectedVehicle = vehicleDatabase.find((v) => v.id === selectedVehicleId) || null;
    const bestMatch = vehicleMatch.best_match;
    const confidence = bestMatch ? Math.round(bestMatch.score * 100) : 0;

    return (
        <Box
            sx={{
                p: 2,
                mb: 2,
                bgcolor: alpha(vortxColors.gold, 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
            }}
        >
            <Typography variant="subtitle2" sx={{ color: vortxColors.gold, mb: 1.5, fontWeight: 600 }}>
                Fleet Vehicle & Charger
            </Typography>

            {/* Extracted specs */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                {vehicleSpecs.battery_capacity_kwh && (
                    <Chip
                        icon={<BatteryChargingFullIcon />}
                        label={`${vehicleSpecs.battery_capacity_kwh} kWh`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: alpha(vortxColors.gold, 0.3), color: vortxColors.white }}
                    />
                )}
                {vehicleSpecs.efficiency_kwh_per_mile && (
                    <Chip
                        label={`${vehicleSpecs.efficiency_kwh_per_mile} kWh/mi`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: alpha(vortxColors.gold, 0.3), color: vortxColors.white }}
                    />
                )}
                {vehicleSpecs.charger_type && (
                    <Chip
                        icon={<EvStationIcon />}
                        label={`${vehicleSpecs.charger_type} (${vehicleSpecs.charger_power_kw || '?'} kW)`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: alpha(vortxColors.gold, 0.3), color: vortxColors.white }}
                    />
                )}
                {vehicleSpecs.range_miles && (
                    <Chip
                        label={`${vehicleSpecs.range_miles} mi range`}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: alpha(vortxColors.gold, 0.3), color: vortxColors.white }}
                    />
                )}
            </Box>

            {/* Vehicle database match */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                    value={selectedVehicle}
                    onChange={(_, newVal) => onVehicleSelect?.(newVal?.id || null)}
                    options={vehicleDatabase}
                    groupBy={(option) => option.category}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Vehicle Type"
                            size="small"
                            helperText={
                                bestMatch && !selectedVehicleId
                                    ? `Suggested: ${bestMatch.name} (${confidence}% match)`
                                    : undefined
                            }
                        />
                    )}
                    sx={{ minWidth: 280, flex: 1 }}
                />
                {confidence > 0 && (
                    <Chip
                        label={`${confidence}% match`}
                        size="small"
                        sx={{
                            bgcolor: confidence >= 80 ? 'rgba(46,125,50,0.2)' : confidence >= 50 ? 'rgba(237,108,2,0.2)' : 'rgba(211,47,47,0.2)',
                            color: confidence >= 80 ? '#2e7d32' : confidence >= 50 ? '#ed6c02' : '#d32f2f',
                        }}
                    />
                )}
            </Box>
        </Box>
    );
}

export default VehicleMatchCard;
